import { StorageService } from "./storage.service";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { Uris } from "./Uris";

@Injectable({
  providedIn: "root"
})
export class FarmService {
  // Lista de granjas que el guard pide al iniciar sesion y que el header reutiliza una vez
  private prefetchedFarms: { query: string; request: Observable<any>; at: number } | null = null;

  constructor(
    public storageService: StorageService,
    private http: HttpClient,
    public router: Router
  ) {}

  getFarmSummary(id) {
    return this.http.get(`${Uris.FARM}${id}/summary/`, this.storageService.headers()).pipe(
      map((resp: any) => {
        return resp;
      })
    );
  }

  getPondSummary(id) {
    return this.http.get(`${Uris.PONDS}${id}/summary/`, this.storageService.headers()).pipe(
      map((resp: any) => {
        return resp;
      })
    );
  }

  kml2farms(value) {
    return this.http.post(`${Uris.FARM}kml2farms/`, value, this.storageService.headers()).pipe(
      map((resp: any) => {
        this.storageService.animation("success", "Éxito", `Coordenadas correctamente insertadas`);
        return resp;
      })
    );
  }

  postClientFarm(value) {
    return this.http.post(`${Uris.FARM}`, value, this.storageService.headers()).pipe(
      map((resp: any) => {
        this.storageService.animation(
          "success",
          "Éxito",
          `Granja ${resp.name} creada correctamente`
        );
        return resp;
      })
    );
  }

  patchClientFarm(value, id) {
    return this.http.patch(`${Uris.FARM}${Number(id)}/`, value, this.storageService.headers()).pipe(
      map((resp: any) => {
        this.storageService.animation(
          "success",
          "Éxito",
          `Granja ${resp.name} editada correctamente`
        );
        return resp;
      })
    );
  }

  getFarms(query) {
    return this.http.get(`${Uris.FARM}${query}`, this.storageService.headers(false)).pipe(
      map((resp: any) => {
        return resp;
      })
    );
  }

  /**
   * Query de la lista de granjas del selector del header (id y nombre del cliente actual).
   * @param clientId Id del cliente actual; vacio si no aplica.
   * @returns Query para `getFarms`.
   */
  sessionFarmsQuery(clientId: any): string {
    const queryClient = clientId ? `&client=${clientId}` : "";
    return `?fields=id,name${queryClient}`;
  }

  /**
   * Pide la lista de granjas y la deja guardada para que el header la reutilice.
   * @param query Query de la lista (ver `sessionFarmsQuery`).
   * @returns Observable con las granjas.
   */
  prefetchFarms(query: string): Observable<any> {
    const request = this.getFarms(query).pipe(shareReplay(1));
    this.prefetchedFarms = { query, request, at: Date.now() };
    return request;
  }

  /**
   * Entrega una sola vez la lista guardada por `prefetchFarms` si coincide el query y es
   * de la carga inicial (menos de 30 s); las siguientes llamadas deben volver a pedirla.
   * @param query Query de la lista que se necesita.
   * @returns Observable guardado o null si no hay uno para ese query.
   */
  takePrefetchedFarms(query: string): Observable<any> | null {
    const prefetched = this.prefetchedFarms;
    this.prefetchedFarms = null;
    const fresh = prefetched && Date.now() - prefetched.at < 30000;
    return fresh && prefetched.query === query ? prefetched.request : null;
  }

  /**
   * Obtiene una granja por id.
   * @param id id de la granja.
   * @param query query string opcional.
   * @param destroy false para que la peticion no se cancele al navegar (componentes del layout).
   * @returns Observable con la granja.
   */
  getFarm(id, query = "", destroy = true) {
    return this.http.get(`${Uris.FARM}${id}/${query}`, this.storageService.headers(destroy)).pipe(
      map((resp: any) => {
        return resp;
      })
    );
  }

  deleteFarm(id) {
    return this.http.delete(`${Uris.FARM}${id}/`, this.storageService.headers()).pipe(
      map((resp: any) => {
        this.storageService.animation("success", "Éxito", `Granja eliminada correctamente`);
        return resp;
      })
    );
  }

  linkFarm(id) {
    return this.http.get(`${Uris.FARM}client/${id}/`, this.storageService.headers()).pipe(
      map((resp: any) => {
        return resp;
      })
    );
  }

  automaticTestPreference(farm, body) {
    return this.http
      .post(`${Uris.FARM}${farm}/tasks/status_test_alert/`, body, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }

  scheduleTestAlert(farm, body) {
    return this.http
      .post(`${Uris.FARM}${farm}/tasks/schedule_test_alert/`, body, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }

  tasksScheduleTestAlert(id, body) {
    return this.http
      .post(`${Uris.FARM}${id}/tasks/schedule_test_alert/`, body, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          this.storageService.animation("success", "Éxito", `Testeo configurado correctamente`);
          return resp;
        })
      );
  }

  tasksStatusTestAlert(id, body) {
    return this.http
      .post(`${Uris.FARM}${id}/tasks/status_test_alert/`, body, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          this.storageService.animation("success", "Éxito", `Testeo configurado correctamente`);
          return resp;
        })
      );
  }

  uploadPondFile(farmID, file) {
    return this.http
      .post(`${Uris.FARM}${farmID}/import_ponds/`, file, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          this.storageService.animation(
            "success",
            "Éxito",
            `Alimentadores importados exitosamente`
          );
          return resp;
        })
      );
  }

  /**
   * Retrieves the configuration thresholds for the given farm.
   * @param id - Farm ID.
   * @param destroy - false para que la peticion no se cancele al navegar (componentes del layout).
   * @returns Observable with the FarmConfig object.
   */
  getFarmConfiguration(id: number, destroy = true) {
    return this.http
      .get(`${Uris.FARM}${id}/configuration/`, this.storageService.headers(destroy))
      .pipe(map((resp: any) => resp));
  }

  /**
   * Partially updates the farm configuration thresholds.
   * @param id - Farm ID.
   * @param body - Partial FarmConfig fields to update.
   * @returns Observable with the updated FarmConfig object.
   */
  patchFarmConfiguration(id: number, body: any) {
    return this.http
      .patch(`${Uris.FARM}${id}/configuration/`, body, this.storageService.headers())
      .pipe(map((resp: any) => resp));
  }

  /**
   * Obtiene las granjas eliminadas (soft-delete).
   * @param query - Query string con filtros y paginación.
   * @returns Observable con la lista paginada de granjas inactivas.
   */
  getInactiveFarms(query: string = "") {
    return this.http
      .get(`${Uris.FARM_INACTIVE}${query}`, this.storageService.headers())
      .pipe(map((resp: any) => resp));
  }

  /**
   * Restaura granjas eliminadas (soft-delete) por sus ids.
   * @param objects_ids - Ids de las granjas a restaurar.
   * @returns Observable con la respuesta del backend.
   */
  restoreFarms(objects_ids: number[]) {
    return this.http
      .patch(`${Uris.FARM_RESTORE}`, { objects_ids }, this.storageService.headers())
      .pipe(map((resp: any) => resp));
  }
}
